"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { isSuccess } from "../../../models/response/base-response";
import DateInputCustom from "../../../components/custom/date-input/date-input-custom";
import ReconciliationService, {
  CustomerQty,
  ItemDebtOverview,
} from "../services/reconciliation-service";

const numberFmt = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const fmt = (v: number) => numberFmt.format(Number(v) || 0);

const toDMY = (ymd: string) => {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-");
  return d && m && y ? `${d}/${m}/${y}` : "";
};
const toYMD = (dmy: string) => {
  if (!dmy) return "";
  const [d, m, y] = dmy.split("/");
  return d && m && y ? `${y}-${m}-${d}` : "";
};
const getDefaultDates = () => {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const f = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return { from: f(first), to: f(now) };
};

const DEBT_COLORS = ["#f59e0b", "#fb923c", "#fbbf24", "#f97316", "#eab308", "#d97706", "#ea580c", "#ca8a04", "#b45309", "#92400e"];
const DEFECT_COLORS = ["#ef4444", "#f43f5e", "#dc2626", "#e11d48", "#b91c1c", "#be123c", "#991b1b", "#9f1239", "#7f1d1d", "#881337"];

const ItemDebtOverviewPage: React.FC = () => {
  const defaults = getDefaultDates();
  const [fromDate, setFromDate] = useState(defaults.from);
  const [toDate, setToDate] = useState(defaults.to);
  const [data, setData] = useState<ItemDebtOverview | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const { fetch: fetchOverview } = ReconciliationService.overview();
  const overviewRef = useRef(fetchOverview);
  useEffect(() => { overviewRef.current = fetchOverview; }, [fetchOverview]);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await overviewRef.current({ from_date: fromDate, to_date: toDate, limit: 10 });
      if (res && isSuccess(res)) setData(res.data as ItemDebtOverview);
    } catch (e) {
      console.error("Failed to load item debt overview:", e);
    } finally {
      setIsLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => { void load(); }, [load]);

  const debtChart = useChartOptions(data?.top_debt || [], DEBT_COLORS);
  const defectChart = useChartOptions(data?.top_non_standard || [], DEFECT_COLORS);

  return (
    <div className="p-4 space-y-4">
      {/* Header + filter */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-indigo-600 flex items-center justify-center text-white text-lg">
            📊
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-800">Báo cáo đối soát đơn hàng</h1>
            <p className="text-sm text-slate-500">
              Tổng hợp nợ đồ · thất thoát · hàng không chuẩn theo khách hàng
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Từ ngày</label>
            <DateInputCustom
              type="date" value={toDMY(fromDate)}
              onChange={(d) => setFromDate(toYMD(d ?? ""))} className="w-full"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Đến ngày</label>
            <DateInputCustom
              type="date" value={toDMY(toDate)}
              onChange={(d) => setToDate(toYMD(d ?? ""))} className="w-full"
            />
          </div>
        </div>
        <p className="mt-3 text-xs text-slate-500 italic">
          Chỉ tính <b>đơn kiểm đếm</b>. Hai bảng <b>Top</b> là số <b>lũy kế tới hiện tại</b> (không phụ thuộc
          khoảng thời gian); khoảng thời gian chỉ áp cho bảng chi tiết cuối trang.
        </p>
      </div>

      {isLoading && (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500">
          Đang tải...
        </div>
      )}

      {!isLoading && data && (
        <>
          {/* Ô tổng */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Card label="Đang nợ khách (hiện tại)" value={fmt(data.totals.debt_now)} unit="cái" tone="amber" />
            <Card label="Hàng không chuẩn (lũy kế)" value={fmt(data.totals.non_standard_all_time)} unit="cái" tone="rose" />
            <Card label="Thất thoát trong kỳ" value={fmt(data.totals.period_lost)} unit="cái" tone="rose" />
            <Card label="Khách có phát sinh" value={fmt(data.totals.period_customers)} unit="khách" />
          </div>

          {/* Top khách đang bị nợ đồ */}
          <TopBlock
            title="Top khách đang bị nợ đồ"
            hint="Số dư nợ tính tới hiện tại — ưu tiên trả bù cho các khách này ở đơn kế tiếp."
            rows={data.top_debt}
            chart={debtChart}
            emptyText="Không có khách nào đang bị nợ đồ."
            valueClass="text-amber-600"
          />

          {/* Top khách có đồ không chuẩn */}
          <TopBlock
            title="Top khách có đồ không chuẩn"
            hint="Đồ khách gửi không đạt chuẩn, bị loại khi giặt (lũy kế) — căn cứ để trao đổi lại với khách."
            rows={data.top_non_standard}
            chart={defectChart}
            emptyText="Chưa ghi nhận hàng không chuẩn."
            valueClass="text-rose-600"
          />

          {/* Bảng chi tiết theo kỳ */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-800">
                Chi tiết theo khách — {toDMY(data.from_date)} → {toDMY(data.to_date)}
              </h3>
              <span className="text-xs text-slate-500">{data.period_rows.length} khách có phát sinh</span>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-3 py-2 text-left">Khách hàng</th>
                    <th className="px-3 py-2 text-center">Nợ phát sinh</th>
                    <th className="px-3 py-2 text-center">Đã trả</th>
                    <th className="px-3 py-2 text-center">Nợ ròng</th>
                    <th className="px-3 py-2 text-center">Thất thoát</th>
                    <th className="px-3 py-2 text-center">Hàng không chuẩn</th>
                    <th className="px-3 py-2 text-center">Tổng chênh</th>
                  </tr>
                </thead>
                <tbody>
                  {data.period_rows.map((r) => (
                    <tr key={r.customer_id} className="border-t hover:bg-slate-50">
                      <td className="px-3 py-2 font-medium text-slate-800">{r.customer_name}</td>
                      <td className="px-3 py-2 text-center text-amber-600 font-semibold">
                        {r.owe ? fmt(r.owe) : ""}
                      </td>
                      <td className="px-3 py-2 text-center text-emerald-600 font-semibold">
                        {r.returned ? fmt(r.returned) : ""}
                      </td>
                      <td className="px-3 py-2 text-center font-bold">
                        {r.net_debt ? fmt(r.net_debt) : "0"}
                      </td>
                      <td className="px-3 py-2 text-center text-rose-600 font-semibold">
                        {r.lost ? fmt(r.lost) : ""}
                      </td>
                      <td className="px-3 py-2 text-center text-orange-600 font-semibold">
                        {r.non_standard ? fmt(r.non_standard) : ""}
                      </td>
                      <td className="px-3 py-2 text-center font-bold text-slate-700">
                        {fmt(r.total_diff)}
                      </td>
                    </tr>
                  ))}
                  {data.period_rows.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-3 py-8 text-center text-slate-500">
                        Không có khách nào phát sinh nợ / thất thoát / hàng không chuẩn trong khoảng thời gian này.
                      </td>
                    </tr>
                  )}
                </tbody>
                {data.period_rows.length > 0 && (
                  <tfoot className="bg-slate-100 font-semibold">
                    <tr>
                      <td className="px-3 py-2">Tổng</td>
                      <td className="px-3 py-2 text-center">{fmt(data.totals.period_owe)}</td>
                      <td className="px-3 py-2 text-center">{fmt(data.totals.period_returned)}</td>
                      <td className="px-3 py-2 text-center">
                        {fmt(data.totals.period_owe - data.totals.period_returned)}
                      </td>
                      <td className="px-3 py-2 text-center">{fmt(data.totals.period_lost)}</td>
                      <td className="px-3 py-2 text-center">{fmt(data.totals.period_non_standard)}</td>
                      <td className="px-3 py-2 text-center" />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

/** Options cho biểu đồ tròn — tách ra vì hai khối dùng chung, chỉ khác bảng màu. */
function useChartOptions(rows: CustomerQty[], colors: string[]) {
  return useMemo(
    () => ({
      series: rows.map((r) => r.quantity),
      options: {
        chart: { type: "donut" as const },
        labels: rows.map((r) => r.customer_name),
        colors,
        legend: { position: "bottom" as const, fontSize: "12px" },
        dataLabels: {
          formatter: (val: number) => `${Math.round(val)}%`,
        },
        tooltip: {
          y: { formatter: (val: number) => `${fmt(val)} cái` },
        },
        plotOptions: {
          pie: {
            donut: {
              labels: {
                show: true,
                total: {
                  show: true,
                  label: "Tổng",
                  formatter: () => `${fmt(rows.reduce((s, r) => s + r.quantity, 0))} cái`,
                },
              },
            },
          },
        },
        noData: { text: "Không có dữ liệu" },
      },
    }),
    [rows, colors],
  );
}

const TopBlock: React.FC<{
  title: string;
  hint: string;
  rows: CustomerQty[];
  chart: { series: number[]; options: any };
  emptyText: string;
  valueClass: string;
}> = ({ title, hint, rows, chart, emptyText, valueClass }) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
    <div className="px-4 py-3 border-b">
      <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
      <p className="text-xs text-slate-500 mt-0.5">{hint}</p>
    </div>
    {rows.length === 0 ? (
      <div className="p-8 text-center text-slate-500">{emptyText}</div>
    ) : (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left w-10">#</th>
                <th className="px-3 py-2 text-left">Khách hàng</th>
                <th className="px-3 py-2 text-right">Số lượng</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.customer_id} className="border-t">
                  <td className="px-3 py-2 text-slate-400">{i + 1}</td>
                  <td className="px-3 py-2 font-medium text-slate-800">{r.customer_name}</td>
                  <td className={`px-3 py-2 text-right font-bold ${valueClass}`}>{fmt(r.quantity)} cái</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-center">
          <ReactApexChart options={chart.options} series={chart.series} type="donut" height={300} width="100%" />
        </div>
      </div>
    )}
  </div>
);

const Card: React.FC<{ label: string; value: string; unit: string; tone?: string }> = ({
  label, value, unit, tone,
}) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
    <div className="text-xs text-slate-500 mb-1">{label}</div>
    <div
      className={`text-2xl font-bold ${
        tone === "amber" ? "text-amber-600" : tone === "rose" ? "text-rose-600" : "text-slate-800"
      }`}
    >
      {value} <span className="text-sm font-normal text-slate-400">{unit}</span>
    </div>
  </div>
);

export default ItemDebtOverviewPage;
